import type { ReactNode } from 'react';
import { AutomationDelivery, AutomationPath } from './automation';
import { IngestPath, TriageLoop, WriteGate } from './ingestion';
import { HybridSearch, RetrievalPath } from './retrieval';
import { FindingLifecycle, SweepPath } from './sweep';
import { WorkedRun } from './worked-run';

/**
 * The diagrams the engineering documents can embed, by name.
 *
 * A registry rather than anything parsed out of the markdown, and that is the whole safety argument:
 * a document names a diagram, this decides what that name draws. The shared Markdown renderer still
 * escapes raw HTML on every surface — KB bodies are ingested content and are not ours to trust — and
 * nothing here changes that, because no markup ever leaves a document.
 *
 * A name that is not here renders as a visible placeholder rather than throwing or disappearing: a
 * missing diagram is a typo in a document, and a document that silently drops a section is worse than
 * one that says which section it lost.
 */
export const DIAGRAMS: Record<string, () => ReactNode> = {
  // The global ingestion flow
  'ingest-path': IngestPath,
  'write-gate': WriteGate,
  'triage-loop': TriageLoop,
  'worked-run': WorkedRun,

  // The retrieval flow
  'retrieval-path': RetrievalPath,
  'hybrid-search': HybridSearch,

  // The sweep flow
  'sweep-path': SweepPath,
  'finding-lifecycle': FindingLifecycle,

  // The automation flow
  'automation-path': AutomationPath,
  'automation-delivery': AutomationDelivery,
};

export function Diagram({ name }: { name: string }) {
  const Component = DIAGRAMS[name];
  if (!Component) {
    return (
      <p className="my-6 rounded-lg border border-dashed border-danger-ring bg-danger-bg px-4 py-3 text-xs text-danger-text">
        Unknown diagram <code className="font-mono">{name}</code>. Add it to{' '}
        <code className="font-mono">src/docs/diagrams/index.tsx</code>.
      </p>
    );
  }
  return <Component />;
}
